import { axiosClient } from "@/shared/api/axios-client";
import { encolarDescarga } from "@/shared/api/descarga-secuencial";
import type {
  CategoriaDocumento,
  DocumentoApi,
} from "@/lib/types";

/**
 * Endpoints del paso "Documentos" · subir / eliminar / descargar.
 */
export const documentosApi = {
  subir: (
    solicitudId: string,
    numeroCfdi: number,
    categoria: CategoriaDocumento,
    file: File,
    /** Tipo del catálogo (P31) · obligatorio cuando categoria === "Adicional". */
    tipoDocumento?: string
  ) => {
    const form = new FormData();
    form.append("archivo", file);
    form.append("categoria", categoria);
    form.append("numeroCfdi", String(numeroCfdi));
    if (tipoDocumento) form.append("tipoDocumento", tipoDocumento);
    return axiosClient
      .post<DocumentoApi>(`/api/solicitudes/${solicitudId}/documentos`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },

  eliminar: (solicitudId: string, documentoId: string, motivo?: string) => {
    const url =
      `/api/solicitudes/${solicitudId}/documentos/${documentoId}` +
      (motivo ? `?motivo=${encodeURIComponent(motivo)}` : "");
    return axiosClient.delete(url).then(() => undefined);
  },

  descargar: async (
    solicitudId: string,
    documentoId: string,
    nombre: string
  ): Promise<void> => {
    // SECUENCIAL por directiva: de una en una, sin paralelas ni ráfagas.
    const resp = await encolarDescarga(() =>
      axiosClient.get<Blob>(
        `/api/solicitudes/${solicitudId}/documentos/${documentoId}/descargar`,
        { responseType: "blob" }
      )
    );
    const url = window.URL.createObjectURL(resp.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = nombre;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Trae el blob del documento + reconstruye el blob URL con el
   * contentType correcto para que iframe/img lo interpreten bien.
   * Devuelve el `blob` original (no consumido) por si el caller quiere
   * leer texto con `blob.text()` para preview de XML/JSON.
   *
   * El caller es dueño de hacer `URL.revokeObjectURL(blobUrl)` cuando
   * termine para liberar memoria del navegador.
   */
  previsualizar: async (
    solicitudId: string,
    documentoId: string
  ): Promise<{ blobUrl: string; contentType: string; blob: Blob }> => {
    // SECUENCIAL por directiva: de una en una, sin paralelas ni ráfagas.
    const resp = await encolarDescarga(() =>
      axiosClient.get<Blob>(
        `/api/solicitudes/${solicitudId}/documentos/${documentoId}/descargar`,
        { responseType: "blob" }
      )
    );
    const contentType =
      String(resp.headers["content-type"] ?? "").split(";")[0].trim() ||
      resp.data.type ||
      "application/octet-stream";
    const blob = new Blob([resp.data], { type: contentType });
    const blobUrl = window.URL.createObjectURL(blob);
    return { blobUrl, contentType, blob };
  },
};
